import { Component } from 'preact';
import CountdownTimerRenderer from '../countdown/CountdownTimerRenderer';
import CardCountdownTimerStyle from '../countdown/styles/CardCountdownTimer.scss';
import CountdownTimer from '../countdown/CountdownTimer';
import style from './styles/RaffleCard.scss';
import RaffleService from '../../services/RaffleService';
import MutationService from '../../services/MutationService';
import ButtonLink from '../button/ButtonLink';
import { Link } from 'react-router-dom';
import CurrencyService from '../../services/CurrencyService';

/**
 * @property {Raffle} props.raffle
 */
export default class RaffleCard extends Component {

	/**
	 * Take in state and configure it to closed
	 *
	 * @param {object} state
	 *
	 * @return {object}
	 */
	toClosedState(state) {
		const updatedState = { ...state };
		updatedState.className = style.closed;
		updatedState.buttonProps.disabled = false;
		updatedState.buttonProps.showArrow = true;
		updatedState.buttonProps.text = 'See details';
		updatedState.status = 'Closing in';
		updatedState.countdownDate = null;
		updatedState.countdownType = null;
		return updatedState;
	}

	/**
	 * Take in state and configure it to coming soon
	 *
	 * @param {object} state
	 * @param {Raffle} raffle
	 *
	 * @return {object}
	 */
	toComingSoonState(state, raffle) {
		const updatedState = { ...state };
		updatedState.className = style['coming-soon'];
		updatedState.status = 'Opening in';
		updatedState.buttonProps.text = 'Raffle not started yet';
		updatedState.buttonProps.disabled = true;
		updatedState.buttonProps.showArrow = false;
		updatedState.countdownDate = raffle.getOpenRegistrationAtAsDate();
		updatedState.countdownType = 'toOpen';
		return updatedState;
	}

	/**
	 * Take in state and configure it to active / closing soon
	 *
	 * @param {object} state
	 * @param {Raffle} raffle
	 *
	 * @return {object}
	 */
	toActiveOrClosingSoonState(state, raffle) {
		const updatedState = { ...state };

		updatedState.buttonProps.disabled = false;
		updatedState.buttonProps.showArrow = true;
		updatedState.buttonProps.text = 'See details';

		if (raffle.isClosingSoon()) {
			updatedState.className = style['closing-soon'];
			updatedState.status = 'Closing soon!';
		}
		else if (raffle.isActive()) {
			updatedState.className = style.active;
			updatedState.status = 'Closing in';
		}

		updatedState.countdownDate = raffle.getCloseRegistrationAtAsDate();
		updatedState.countdownType = 'toClose';

		return updatedState;
	}

	/**
	 * Update component state when countdown finishes
	 */
	onCountdownComplete = (delta) => {
		// Wait a second to avoid precision issues
		setTimeout(() => {
			// If we were counting to close, we need to "disable" the raffle
			if (this.state.countdownType === 'toClose') {
				const closedState = this.toClosedState(this.state);
				this.setState(closedState);
			}
			// If we were counting to open, we switch to active state and start countdown to close
			else if (this.state.countdownType === 'toOpen') {
				const activeState = this.toActiveOrClosingSoonState(this.state, this.props.raffle);
				this.setState(activeState);
			}
		}, 1000);
	};

	buildUpdatedState(raffle) {
		let updatedState = { ...this.state };

		if (raffle.isComingSoon()) {
			updatedState = this.toComingSoonState(this.state, raffle);
		}
		else if (raffle.isActive() || raffle.isClosingSoon()) {
			updatedState = this.toActiveOrClosingSoonState(this.state, raffle);
		}
		else if (raffle.isClosed()) {
			updatedState = this.toClosedState(this.state);
		}

		updatedState.buttonProps.to = RaffleService.getDetailUrl(raffle);

		return updatedState;
	}

	constructor(props) {
		super(props);

		this.state = {
			countdownType: null, // toClose or toOpen
			countdownDate: null,
			className: '',
			status: 'Unknown status',
			buttonProps: {
				className: style['detail-button'],
				disabled: false,
				showArrow: true,
				text: 'See details',
				to: ''
			}
		};

		const { raffle } = props;
		if (raffle) {
			this.state = this.buildUpdatedState(raffle);
		}
	}

	/**
	 * Initialize component state based on raffle & current time.
	 */
	componentWillReceiveProps(nextProps, nextContext) {
		const { raffle } = nextProps;
		if (raffle) {
			this.setState(this.buildUpdatedState(raffle));
		}
	}

	render({ raffle }, { countdownType, countdownDate, className, status, buttonProps }) {
		return (
			<div className={`${style.card} ${className}`}>
				<div className={style['inner-card']}>
					{ buttonProps.to ? (
						<Link to={buttonProps.to}>
							<Image raffle={raffle} />
						</Link>
					) : (
						<Image raffle={raffle} />
					)}
					<Brand raffle={raffle} />
					<Link to={buttonProps.to}>
						<Model raffle={raffle} />
					</Link>
					<Type raffle={raffle} />
					<Price raffle={raffle} />
					{ countdownDate === null ? (
						<ClosedStatuses raffle={raffle} />
					) : ([
						<hr className={style['horizontal-separator']} />,
						<Status status={status} />,
						<CountdownTimer renderer={CountdownTimerRenderer} style={CardCountdownTimerStyle} date={countdownDate} onComplete={this.onCountdownComplete} />
					])}
					<ButtonLink {...buttonProps} />
				</div>
			</div>
		);
	}
}

const Image = ({ raffle }) => (<div className={style.image} style={`background-image: url("${RaffleService.getImageUrl(raffle.getMainImage())}")`} />);
const Brand = ({ raffle }) => (<div className={style.brand}>{raffle.brand}</div>);
const Model = ({ raffle }) => (<h3 className={style.model}>{raffle.getTitle(MutationService.getMutationCode())}</h3>);
const Type = ({ raffle }) => (<div className={style.type}>{raffle.getSubtitle(MutationService.getMutationCode())}</div>);
const Price = ({ raffle }) => (<div className={style.price}>{CurrencyService.format(raffle.getPrice(CurrencyService.getCurrencyCode()))}</div>);
const Status = ({ status }) => (<div className={style.status}>{status}</div>);

const ClosedStatuses = ({ raffle }) => (
	<div className={style['closed-statuses']}>
		{ raffle.finished ? (
			<span>Closed</span>
		) : (
			<span>
				Registration closed<br />
				Waiting for draw
			</span>
		) }
	</div>
);


// WEBPACK FOOTER //
// ./components/raffle/RaffleCard.js